import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * AuthModal — sign in or create account.
 * Open/close + mode controlled via AuthContext (useAuth().openAuthModal('login'|'signup')).
 */
export default function AuthModal() {
    const {
        authModalOpen,
        authModalMode,
        closeAuthModal,
        login,
        signup,
        openAuthModal,
    } = useAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!authModalOpen) {
            setBusy(false);
            // Don't wipe email between modal toggles; do wipe password.
            setPassword("");
        }
    }, [authModalOpen]);

    const isSignup = authModalMode === "signup";

    const onSubmit = async (e) => {
        e.preventDefault();
        if (busy) return;
        if (!email || !password) {
            toast.error("Email and password required");
            return;
        }
        if (isSignup && password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setBusy(true);
        try {
            if (isSignup) {
                await signup(email.trim(), password);
                toast.success("Account created · welcome", { description: email });
            } else {
                await login(email.trim(), password);
                toast.success("Signed in", { description: email });
            }
            closeAuthModal();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.message ||
                "Something went wrong";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={authModalOpen} onOpenChange={(o) => (o ? null : closeAuthModal())}>
            <DialogContent
                data-testid="auth-modal"
                className="
                    bg-tca-bg-panel/95 backdrop-blur-xl
                    border border-tca-cyan/20
                    text-tca-ink max-w-md
                    sm:max-w-md
                "
                style={{ boxShadow: "0 0 48px rgba(0,219,255,0.18)" }}
            >
                <DialogTitle className="font-mono-tight text-[11px] uppercase tracking-[0.32em] text-tca-cyan/90">
                    // {isSignup ? "Create Operator Account" : "Operator Sign In"}
                </DialogTitle>
                <DialogDescription className="sr-only">
                    {isSignup
                        ? "Create an account to track your ecosystem entitlements."
                        : "Sign in to your Creator's Agent operator account."}
                </DialogDescription>

                <div className="font-display text-2xl sm:text-3xl tracking-[-0.02em] mt-1">
                    {isSignup ? "Open your operator account." : "Resume your build path."}
                </div>
                <p className="text-tca-ink-muted text-sm mt-1.5">
                    {isSignup
                        ? "We provision an entitlement record on signup. Your purchase activates the system."
                        : "Sign in to see your unlocked letters, phases, and active modules."}
                </p>

                <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="auth-form">
                    <div className="space-y-1.5">
                        <Label
                            htmlFor="auth-email"
                            className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted"
                        >
                            Email
                        </Label>
                        <Input
                            id="auth-email"
                            type="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="auth-email-input"
                            className="bg-tca-bg-deep/60 border-tca-ink-dim text-tca-ink focus-visible:ring-tca-cyan focus-visible:border-tca-cyan/60"
                            placeholder="you@domain.com"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label
                            htmlFor="auth-password"
                            className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted"
                        >
                            Password
                        </Label>
                        <Input
                            id="auth-password"
                            type="password"
                            autoComplete={isSignup ? "new-password" : "current-password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="auth-password-input"
                            className="bg-tca-bg-deep/60 border-tca-ink-dim text-tca-ink focus-visible:ring-tca-cyan focus-visible:border-tca-cyan/60"
                            placeholder={isSignup ? "min 8 characters" : "••••••••"}
                            required
                            minLength={isSignup ? 8 : undefined}
                        />
                    </div>

                    <button
                        type="submit"
                        data-testid="auth-submit"
                        disabled={busy}
                        className="
                            mt-2 w-full inline-flex items-center justify-center gap-2
                            font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs
                            py-3.5 border border-tca-cyan text-tca-cyan-hi
                            bg-gradient-to-b from-[rgba(0,219,255,0.22)] to-[rgba(0,219,255,0.06)]
                            hover:text-white hover:shadow-[0_0_28px_rgba(0,219,255,0.45)]
                            rounded-full transition-all duration-300
                            disabled:opacity-60 disabled:cursor-not-allowed
                        "
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {isSignup ? "Create account" : "Sign in"}
                    </button>
                </form>

                <div className="mt-5 text-center font-mono-tight text-[11px] tracking-[0.18em] text-tca-ink-muted">
                    {isSignup ? "Already have an account?" : "New here?"}{" "}
                    <button
                        type="button"
                        onClick={() => openAuthModal(isSignup ? "login" : "signup")}
                        data-testid="auth-toggle-mode"
                        className="text-tca-cyan hover:text-tca-cyan-hi underline underline-offset-4"
                    >
                        {isSignup ? "Sign in" : "Create one"}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
